import React, {useEffect} from 'react';
import i18next from 'i18next';
import {Admin, CustomRoutes, Resource} from 'react-admin';

import dataProvider from 'config/providers/dataProvider';
import {i18nProvider} from 'config/i18n';
import polyglotI18nProvider from 'ra-i18n-polyglot';
import './i18n';

import ProjectList from './ProjectList';
import CustomLayout from './components/CustomLayout';
import IIRSAProjectDetail from './screens/ProjectDetail';
import Analytics from './screens/Analytics';
import {createTheme, ThemeProvider} from "@mui/material";
import {Route} from "react-router-dom";
import ElementsMap from "app/screens/Dashboard/screens/ProjectsIIRSA/screens/ElementsMap";
import RoutesList from "~screens/Dashboard/screens/ProjectsIIRSA/RoutesList";
import {useLocation, useNavigate} from "react-router-dom";
import {getRoutes, getRouteProjects} from '~services/RoutesService';
import {Project} from "~screens/Dashboard/screens/Projects/types";
import {Route as RouteObj} from '~services/RoutesService';
import Home from '~screens/Home';
import { getProjects } from '~services/ProjectsService';

const rai18nProvider = polyglotI18nProvider(i18nProvider, i18next.language, [{locale: 'en', name: 'English'}, {
    locale: 'es',
    name: 'Español'
}, {
    locale: 'pt',
    name: 'Portuguese'
}]);

// A territory's map asks for its name, its Acciones and its geometry at once: one request serves all three.
const pendingTerritories = new Map<string, Promise<RouteObj>>();
const loadTerritory = (id: string): Promise<RouteObj> => {
    let request = pendingTerritories.get(id);
    if (!request) {
        request = getRouteProjects(id);
        pendingTerritories.set(id, request);
        const forget = () => pendingTerritories.delete(id);
        request.then(forget, forget);
    }
    return request;
};

const theme = createTheme({
    palette: {
        primary: {
            light: '#FAFAFA',
            main: '#384078'
        },
        secondary: {
            main: '#90cc94'
        }
    }
});

function ProjectsIIRSA() {
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (location.pathname === '/') {
            navigate('/home');
        }
    }, [navigate, location]);

    const listPathPromise = (params: any) => new Promise<string>((resolve) => {
        const {id} = params;
        resolve(`/territories/${id}/show`);
    });

    const listTitlePromise = (params: any) => new Promise<string>((resolve) => {
        const {id} = params;
        if (!id) {
            resolve('');
            return;
        }
        loadTerritory(id).then(route => {
            resolve(route.name)
        });
    });

    const dataLoader = (params, location) => {
        const {id} = params;
        if (!id) {
            return new Promise<Project[]>((resolve) => resolve([]));
        }
        return loadTerritory(id).then(route => route.actionsList || []);
    };

    const projectsDataLoader = (params, location) => {
        // Parse the search params
        const searchParams = new URLSearchParams(location.search);
        const filterParam = searchParams.get('filter');
        const sortParam = searchParams.get('sort') || '';
        const orderParam = searchParams.get('order') || '';
        const pageParam = searchParams.get('page') || '';
        const limitParam = searchParams.get('perPage') || '';

        let queryString = '';
        if (filterParam) {
            try {
                // Parse the filter JSON
                const filterObj = JSON.parse(filterParam);
                if (sortParam) {
                    filterObj.sort = sortParam;
                }
                if (orderParam) {
                    filterObj.order = orderParam;
                }
                if (pageParam) {
                    filterObj.page = pageParam;
                }
                if (limitParam) {
                    filterObj.limit = limitParam;
                }

                // Convert filter object to query params
                const queryParams = new URLSearchParams();
                Object.entries(filterObj).forEach(([key, value]) => {
                    if (value !== null && value !== undefined && value !== '') {
                        queryParams.append(key, String(value));
                    }
                });

                queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
            } catch (e) {
                console.error('Failed to parse filter:', e);
            }
        }

        return getProjects(queryString).then(data => data?.data?.page || []);
    }

    const routeDataLoader = (params, location) => {
        const {id} = params;
        if (!id) {
            return new Promise<RouteObj | undefined>((resolve) => resolve(undefined));
        }
        return loadTerritory(id);
    }

    return (
        <ThemeProvider theme={theme}>
            <Admin i18nProvider={rai18nProvider} dataProvider={dataProvider}
                   layout={CustomLayout}
            >
                <CustomRoutes>
                    <Route path="/home" element={<Home />} />
                </CustomRoutes>
                <Resource name="actions" list={ProjectList} show={IIRSAProjectDetail}>
                    <Route path={'map'}
                           element={<ElementsMap listPath={'/actions'} listTitle={i18next.t('projects-iirsa:projects') as string}
                                                 dataLoader={projectsDataLoader} selectable={true}/>}/>
                </Resource>

                <Resource name={"territories"} list={RoutesList} show={ProjectList}>
                    <Route path="charts" element={<Analytics/>}/>
                    <Route index path="map"
                           element={<ElementsMap listPath={'/territories'} dataLoader={(params) => getRoutes()} selectable={false}/>}/>
                    <Route path={':id/actions'} element={<ProjectList/>}/>
                    <Route path={':id/show/map'}
                           element={<ElementsMap listPath={listPathPromise} listTitle={listTitlePromise}
                                                 dataLoader={dataLoader} routeDataLoader={routeDataLoader} selectable={true}/>}/>
                </Resource>
            </Admin>
        </ThemeProvider>
    );
}

export default ProjectsIIRSA;
